import React, { useEffect } from 'react';

import { ActionState, usePageWatchUpdate } from './usePageWatchUpdate';
import { ChangeWatchForm } from './ChangeWatchForm';
import { getButtonModifier, iconLookup } from './iconLookup';
import { BtnModifier } from '../../../../components/molecules/Btn';
import { Namespace } from '../../../../translation/namespace';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import useCollapsibleOverlay from '../../../../hooks/useCollapsibleOverlay';
import { sendLiveRegionMessage } from '../../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { Button } from '../../../../components/molecules/Btn/Button';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { Icons } from '../../../../components/atoms/SVG';
import bem from '../../../../lib/bemUtils/bem';
import { CollapsibleModifier } from '../../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay.modifier';

export type PageWatchState =
  | 'watchNotSupported'
  | 'notWatched'
  | 'singleWatched'
  | 'parentWatched'
  | 'treeWatched'
  | 'neverWatched';

export type PageWatchProps = { forms: Partial<Record<PageWatchState, ChangeWatchForm>>; state: PageWatchState };

function getActionStateModifier(actionState: ActionState, watchState: PageWatchState) {
  switch (actionState.type) {
    case 'sending':
      return [BtnModifier.MINOR_ACTION_BUSY, ...getButtonModifier(watchState)];
    case 'fail':
      return [BtnModifier.MINOR_ACTION_FAILED];
    default:
      return getButtonModifier(watchState);
  }
}

export function PageWatch(props: PageWatchProps) {
  const { t } = useSonTranslation(Namespace.PageInteractionsWatchButton);
  const { dispatch, actionState, data } = usePageWatchUpdate(props);

  const {
    toggleProps,
    collapsibleProps: { isOpen: _, ...collapsibleProps },
    closeCollapsible,
  } = useCollapsibleOverlay();

  const liveRegionText = actionState.type === 'initial' ? '' : t(`status.${actionState.type}`);

  useEffect(() => sendLiveRegionMessage(liveRegionText), [liveRegionText]);

  /**
   * toggleCollapsible only on success or fail,
   * because then, we have the collapsible button changed
   * and it carries current state as name (fail as well!)
   * */
  useEffect(() => {
    if (actionState.type === 'success' || actionState.type === 'fail') {
      closeCollapsible();
    }
  }, [actionState, closeCollapsible]);

  const modifiers = [
    ...getActionStateModifier(actionState, data.state),
    BtnModifier.TRANSPARENT,
    BtnModifier.MICRO_INTERACTION,
    BtnModifier.MINOR_ACTION,
  ];

  const text =
    actionState.type === 'fail'
      ? `${t('changeWatch')} (${t('currently')}: ${t(`${actionState.type}.collapsibleButtonText`)})`
      : `${t('changeWatch')} (${t('currently')}: ${t(`${data.state}.collapsibleButtonText`)})`;
  return (
    <div className="page-interactions__button-container">
      <Button
        tooltipPlacement={TooltipPlacement.TOP}
        hideTooltipFromScreenReader
        title={text}
        modifier={modifiers}
        {...toggleProps}
      >
        <Button.Icon
          text={text}
          svgSpriteId={actionState.type !== 'fail' ? iconLookup[data.state] : Icons.FEEDBACK_ERROR}
        />
      </Button>
      <div {...collapsibleProps} className={bem('collapsible-overlay', CollapsibleModifier.PAGE_INTERACTIONS_WATCH)}>
        <div className="page-interactions__watch-container">
          {Object.entries(data.forms).map(([action, form]) => (
            <div className="page-interactions__button-container" key={action}>
              <ChangeWatchForm state={action as PageWatchState} form={form} dispatchAction={dispatch} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
